import Icon from '@ant-design/icons'
import clsx from 'clsx'
import { FormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { getMilestoneInitialValues } from './helpers/get-milestone-initial-values'
import styles from './milestone-info.module.scss'
import { MilestoneValidationSchema } from './validators/milestone-validation-schema'
import { getIcon, IconEnum } from '../../../../assets/icons'
import { useFormik } from '../../../../common/hooks/formik'
import { PureButton } from '../../../../common/ui/pure-button/pure-button'
import { PureInput } from '../../../../common/ui/pure-input/pure-input'
import { PureSwitcher } from '../../../../common/ui/pure-switcher/pure-switcher'
import { RouteEnum } from '../../../../constants/routes.enum'
import { MilestoneItem } from '../../../../entities/workflow/types/workflow.types'
import { MilestoneEditForm } from '../../milestone/types/milestone-types'

type MilestoneInfoProps = {
  milestone?: MilestoneItem
  onSave: (values: MilestoneEditForm) => void
}

export const MilestoneInfo = ({ onSave, milestone }: MilestoneInfoProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const initialValues: MilestoneEditForm | undefined = milestone
    ? {
        name: milestone.name,
        users: null,
        mobileApp: false,
        shouldBeChecked: milestone.shouldBeChecked,
      }
    : undefined

  const formik = useFormik<MilestoneEditForm>({
    initialValues: getMilestoneInitialValues(initialValues),
    validationSchema: () => MilestoneValidationSchema(t),
    enableReinitialize: true,
    onSubmit: () => {},
  })

  const handleGoBack = () => {
    navigate(RouteEnum.WORKSPACE + '/' + RouteEnum.MILESTONES)
  }

  const handleShouldChange = (value: boolean) => {
    formik.setFieldValue('shouldBeChecked', value)
  }

  const handleMobileAppChange = (value: boolean) => {
    formik.setFieldValue('mobileApp', value)
  }

  const handleSave = async () => {
    const err = await formik.validateForm()
    if (!Object.values(err).length) {
      onSave?.(formik.values)
    } else {
      toast.error('Milestone incorrect')
    }
  }

  return (
    <FormikContext.Provider value={formik as any}>
      <div className={clsx(styles.wrapper, 'h-100 pt-3 px-2')}>
        <div className={clsx(styles.formWrapper)}>
          <h6>
            <Icon
              className={styles.backIcon}
              component={getIcon(IconEnum.ArrowLeft)}
              onClick={handleGoBack}
            />
            <span className="ml-1">{t('formBuilder.content.header')}</span>
          </h6>
          <hr />
          <div className="mt-1 px-2 d-flex flex-column justify-content-between">
            <div>
              <PureInput
                {...formik.getFieldProps('name')}
                error={formik.errors.name}
                label={t('milestone.form.nameLabel')}
                placeholder={t('milestone.form.namePlaceholder')}
                size="small"
              />
              {/*<PureSelect*/}
              {/*  {...formik.getFieldProps('users')}*/}
              {/*  error={formik.errors.name}*/}
              {/*  label={t('milestone.form.usersLabel')}*/}
              {/*  options={[]}*/}
              {/*  placeholder={t('milestone.form.usersPlaceholder')}*/}
              {/*  size="small"*/}
              {/*/>*/}
              <div className="mt-2">
                <PureSwitcher
                  isChecked={formik.values.shouldBeChecked}
                  label={[t('milestone.form.shouldBeCheckedLabel'), t('milestone.form.shouldBeCheckedLabel')]}
                  onChange={handleShouldChange}
                  position="left"
                />
              </div>
              <div className="mt-2">
                <PureSwitcher
                  isChecked={formik.values.mobileApp}
                  label={[t('milestone.form.mobileAppLabel'), t('milestone.form.mobileAppLabel')]}
                  onChange={handleMobileAppChange}
                  position="left"
                />
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(styles.buttonsWrap, 'p-2 d-flex justify-content-between align-items-center')}>
          {!!milestone ? (
            <PureButton
              className={clsx(styles.deleteBtn, 'btn btn-outline-danger')}
              size="small"
              type="text"
            >
              {milestone ? t('common.delete') : t('common.cancel')}
            </PureButton>
          ) : (
            <div></div>
          )}
          <PureButton
            onClick={handleSave}
            size="small"
            type="primary"
          >
            {milestone ? t('common.update') : t('common.create')}
          </PureButton>
        </div>
      </div>
    </FormikContext.Provider>
  )
}
